import { useMemo } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  useGetInspectionTrends,
  useGetDefectDistribution,
  useGetSeverityDistribution,
  useGetClassificationsStats,
  getDefectTypeLabel,
  getSeverityLabel,
} from '../hooks/useQueries';
import { BarChart3, TrendingUp, PieChart as PieIcon, Activity } from 'lucide-react';

function formatTrendDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const SEVERITY_COLORS: Record<string, string> = {
  good: '#22c55e',
  minorDefect: '#f59e0b',
  majorDefect: '#ef4444',
};

const DEFECT_COLORS: Record<string, string> = {
  scratch: '#3b82f6',
  crack: '#ef4444',
  warp: '#f59e0b',
  discoloration: '#8b5cf6',
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded px-3 py-2 text-xs shadow-card-dark">
        {label && <p className="text-muted-foreground mb-1">{label}</p>}
        {payload.map((p: any, i: number) => (
          <p key={i} style={{ color: p.color || p.fill }} className="font-semibold">
            {p.name}: {p.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({
  cx, cy, midAngle, innerRadius, outerRadius, percent, name,
}: any) => {
  if (percent < 0.05) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight="bold">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

function ChartCard({
  title,
  subtitle,
  icon: Icon,
  loading,
  empty,
  children,
}: {
  title: string;
  subtitle: string;
  icon: React.ElementType;
  loading: boolean;
  empty: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-card border border-border rounded-lg p-5 shadow-card-dark">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <Icon className="w-4 h-4 text-electric-blue opacity-60" />
      </div>
      {loading ? (
        <div className="h-52 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
        </div>
      ) : empty ? (
        <div className="h-52 flex items-center justify-center text-muted-foreground text-sm">
          No data available yet
        </div>
      ) : (
        children
      )}
    </div>
  );
}

export default function Analytics() {
  const { data: trends = [], isLoading: trendsLoading } = useGetInspectionTrends();
  const { data: defectDist = [], isLoading: defectLoading } = useGetDefectDistribution();
  const { data: severityDist = [], isLoading: severityLoading } = useGetSeverityDistribution();
  const { data: classStats, isLoading: classLoading } = useGetClassificationsStats();

  const trendData = useMemo(() => {
    return trends.map(([ts, count]) => ({
      date: formatTrendDate(ts),
      inspections: Number(count),
    }));
  }, [trends]);

  const defectData = useMemo(() => {
    return defectDist.map(([type, count]) => ({
      name: getDefectTypeLabel(type),
      count: Number(count),
      type: type as string,
    }));
  }, [defectDist]);

  const severityData = useMemo(() => {
    return severityDist.map(([sev, count]) => ({
      name: getSeverityLabel(sev),
      value: Number(count),
      key: sev as string,
    }));
  }, [severityDist]);

  const acceptanceData = useMemo(() => {
    if (!classStats) return [];
    return classStats.rejectionStats.map(([sev, count]) => ({
      name: getSeverityLabel(sev),
      value: Number(count),
      key: sev as string,
    }));
  }, [classStats]);

  const totalInspections = useMemo(() => {
    return severityData.reduce((sum, d) => sum + d.value, 0);
  }, [severityData]);

  const acceptanceRate = useMemo(() => {
    if (totalInspections === 0) return 0;
    const good = severityData.find(d => d.key === 'good')?.value ?? 0;
    return Math.round((good / totalInspections) * 100);
  }, [severityData, totalInspections]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-foreground tracking-tight">Analytics</h2>
        <p className="text-xs text-muted-foreground mt-0.5 font-mono">
          Aggregated inspection statistics and trend analysis
        </p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Total Inspections',
            value: totalInspections,
            color: 'text-electric-blue',
          },
          {
            label: 'Acceptance Rate',
            value: `${acceptanceRate}%`,
            color: 'text-status-success',
          },
          {
            label: 'Defect Types Tracked',
            value: defectData.filter(d => d.count > 0).length,
            color: 'text-status-warning',
          },
          {
            label: 'Days with Data',
            value: trendData.length,
            color: 'text-electric-blue',
          },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-card border border-border rounded-lg p-4 shadow-card-dark">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">{label}</p>
            <p className={`text-2xl font-black ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Charts grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Inspection Volume Trend */}
        <ChartCard
          title="Inspection Volume Trend"
          subtitle="Daily inspection count over time"
          icon={TrendingUp}
          loading={trendsLoading}
          empty={trendData.length === 0}
        >
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trendData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.25 0.008 240)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'oklch(0.55 0.01 240)' }} />
              <YAxis tick={{ fontSize: 10, fill: 'oklch(0.55 0.01 240)' }} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="inspections"
                name="Inspections"
                stroke="oklch(0.72 0.18 220)"
                strokeWidth={2}
                dot={{ fill: 'oklch(0.72 0.18 220)', r: 3 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Defect Type Breakdown */}
        <ChartCard
          title="Defect Type Breakdown"
          subtitle="Count by defect category"
          icon={BarChart3}
          loading={defectLoading}
          empty={defectData.every(d => d.count === 0)}
        >
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={defectData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.25 0.008 240)" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'oklch(0.55 0.01 240)' }} />
              <YAxis tick={{ fontSize: 10, fill: 'oklch(0.55 0.01 240)' }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Count" radius={[2, 2, 0, 0]}>
                {defectData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={DEFECT_COLORS[entry.type] || 'oklch(0.72 0.18 220)'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Severity Distribution */}
        <ChartCard
          title="Severity Distribution"
          subtitle="Breakdown by classification"
          icon={Activity}
          loading={severityLoading}
          empty={severityData.every(d => d.value === 0)}
        >
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="60%" height={200}>
              <PieChart>
                <Pie
                  data={severityData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={80}
                  dataKey="value"
                >
                  {severityData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={SEVERITY_COLORS[entry.key] || '#64748b'}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2">
              {severityData.map(entry => (
                <div key={entry.key} className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                    style={{ background: SEVERITY_COLORS[entry.key] || '#64748b' }}
                  />
                  <span className="text-xs text-muted-foreground flex-1">{entry.name}</span>
                  <span className="text-xs font-mono font-semibold text-foreground">{entry.value}</span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>

        {/* Acceptance vs Rejection */}
        <ChartCard
          title="Acceptance vs Rejection"
          subtitle="Pass/fail ratio by classification"
          icon={PieIcon}
          loading={classLoading}
          empty={acceptanceData.every(d => d.value === 0)}
        >
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="60%" height={200}>
              <PieChart>
                <Pie
                  data={acceptanceData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  labelLine={false}
                  label={renderCustomizedLabel}
                  dataKey="value"
                >
                  {acceptanceData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={SEVERITY_COLORS[entry.key] || '#64748b'}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-3">
              {acceptanceData.map(entry => {
                const pct = totalInspections > 0
                  ? Math.round((entry.value / totalInspections) * 100)
                  : 0;
                return (
                  <div key={entry.key}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ background: SEVERITY_COLORS[entry.key] || '#64748b' }}
                        />
                        <span className="text-xs text-muted-foreground">{entry.name}</span>
                      </div>
                      <span className="text-xs font-mono font-semibold text-foreground">{pct}%</span>
                    </div>
                    <div className="h-1 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${pct}%`,
                          background: SEVERITY_COLORS[entry.key] || '#64748b',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </ChartCard>
      </div>
    </div>
  );
}
